import { useTranslations } from 'next-intl';
import { type FC, useEffect } from 'react';
import { CommonSelect, type SelectOption } from '@/app/components/shared/CommonSelect';
import { SettingsItem } from '@/app/components/shared/SettingsItem';
import { toast } from '@/app/components/ui/use-toast';
import { useAudioLipSyncConstraint } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { OptionProps } from '@/app/types';

/**
 * 音效&语音选项组件
 *
 * @param value 当前开关状态
 * @param onChange 值变更回调
 */
export const AudioAndLipSyncOption: FC<OptionProps<{ with_audio: boolean; lip_sync: boolean }>> = ({
  value,
  onChange,
}) => {
  const t = useTranslations();
  const referencePrompts = useStore((state) => state.character2videoFormParams.referencePrompts);

  const { isSfxVoiceDisabled } = useAudioLipSyncConstraint(referencePrompts);

  // 计算当前下拉选中值
  const selectedValue: 'none' | 'sfx' | 'sfx_voice' = !value?.with_audio
    ? 'none'
    : value?.lip_sync
      ? 'sfx_voice'
      : 'sfx';

  // 当 sfx_voice 被禁用且当前为 sfx_voice 时，自动切换为 sfx
  useEffect(() => {
    if (isSfxVoiceDisabled && selectedValue === 'sfx_voice') {
      onChange({ with_audio: true, lip_sync: false });
      toast({
        message: t('create.createForm.settings.sound.optionSfxVoice.autoSwitchTip2'),
      });
    }
  }, [isSfxVoiceDisabled, selectedValue]);

  const options: SelectOption[] = [
    {
      value: 'none',
      label: t('create.createForm.settings.sound.optionNoDubbing.text'),
    },
    {
      value: 'sfx',
      label: t('create.createForm.settings.sound.optionSfxOnly.text'),
    },
    {
      value: 'sfx_voice',
      label: t('create.createForm.settings.sound.optionSfxVoice.text'),
      disabled: isSfxVoiceDisabled,
      tooltip: isSfxVoiceDisabled ? t('create.createForm.settings.sound.optionSfxVoice.disabledTip2') : undefined,
    },
  ];

  return (
    <SettingsItem
      info={t('create.createForm.settings.sound.info')}
      title={t('create.createForm.settings.sound.title')}
      extra={
        <div className="scale-[.85] rounded-10 border border-system-white/40 px-1.5 text-system-white/40 text-xs">
          Beta
        </div>
      }
    >
      <CommonSelect
        value={selectedValue}
        options={options}
        onValueChange={(val) => {
          if (val === 'none') {
            onChange({ with_audio: false, lip_sync: false });
          } else if (val === 'sfx') {
            onChange({ with_audio: true, lip_sync: false });
          } else {
            onChange({ with_audio: true, lip_sync: true });
          }
        }}
      />
    </SettingsItem>
  );
};
